import { HTMLAttributes, KeyboardEvent } from 'react'
import Link from 'next/link'

import Typography from '@/components/base/typography'
import { useStore } from '@/contexts/store'

import { cn } from '@/lib/utils'
import { SalesPoint, SalesPointCategory } from '@/types/sales-network'

export type StoreCardProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  'onSelect'
> & {
  store: SalesPoint
  categories?: SalesPointCategory[]
  isSelected?: boolean
  onSelect?: (_store: SalesPoint) => void
}

const StoreCard = ({
  store,
  isSelected,
  onSelect,
  className,
  ...props
}: StoreCardProps) => {
  const { strings } = useStore()
  const storeAddress = store?.acf?.place?.address
  const typology = store.taxonomies?.['store-typology']?.[0]?.name

  const handleClick = () => {
    onSelect?.(store)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onSelect?.(store)
    }
  }

  return (
    <div
      data-store-id={store.id}
      role="button"
      tabIndex={0}
      aria-label={`${strings?.labels?.sales_network_see_details || 'Vedi dettagli'}: ${store.title.rendered}`}
      aria-pressed={isSelected}
      className={cn(
        'group cursor-pointer border-b border-[rgba(255,255,255,0.1)] px-spacing-xs py-spacing-md',
        'flex items-center gap-spacing-xs',
        'transition-colors duration-300',
        {
          'bg-[rgba(255,255,255,0.1)]': isSelected
        },
        className
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      {...props}
    >
      {/* Content */}
      <div className="flex flex-1 flex-col items-start gap-y-spacing-xs">
        {typology ? (
          <Typography
            variant="small"
            component="span"
            className="condensed text-text-primary-dark"
          >
            {typology}
          </Typography>
        ) : null}

        <Typography
          variant="large"
          component="h3"
          className="text-text-heading-dark"
        >
          {store.title.rendered}
        </Typography>

        {store?.storeDistance ? (
          <Typography variant="small" className="text-text-primary-dark">
            {store.storeDistance}
          </Typography>
        ) : null}

        {storeAddress ? (
          <Typography variant="small" className="text-text-primary-dark">
            {storeAddress}
          </Typography>
        ) : null}

        <Link
          href={store?.link}
          scroll={false}
          className={cn(
            'group flex h-[3.2rem] items-center border border-[rgba(255,255,255,0.3)] px-spacing-2xs text-text-secondary-dark transition-all duration-300 hover:bg-[#F2F2F2] hover:text-text-button-primary-dark'
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <Typography variant="small" className={cn('condensed')}>
            {strings?.labels?.sales_network_see_details}
          </Typography>
        </Link>
      </div>
    </div>
  )
}

export default StoreCard
